import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, X } from 'lucide-react';
export function TextVersionToggle() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  return <>
      <motion.button onClick={() => setOpen(true)} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1
    }} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} className="fixed top-6 right-6 z-40 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white text-sm font-medium rounded-full flex items-center gap-2 transition-colors" aria-label="Read text version of the concept" data-id="element-348">
        <FileText className="w-4 h-4" data-id="element-349" />
        Read text version
      </motion.button>

      <AnimatePresence data-id="element-350">
        {open && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-solventum-dark/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-12" onClick={() => setOpen(false)} data-id="element-351">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 30,
          scale: 0.98
        }} transition={{
          duration: 0.3
        }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-2xl max-w-3xl w-full my-auto p-8 md:p-12 shadow-2xl" data-id="element-352">
              <button onClick={() => setOpen(false)} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-solventum-lightTeal hover:bg-solventum-medTeal/30 flex items-center justify-center transition-colors" aria-label="Close" data-id="element-353">
                <X className="w-5 h-5 text-solventum-dark" data-id="element-354" />
              </button>

              <div className="mb-8" data-id="element-355">
                <div className="text-xs font-semibold text-solventum-teal tracking-wider mb-3" data-id="element-356">
                  CONCEPT SUMMARY
                </div>
                <h2 className="font-editorial text-3xl md:text-4xl font-bold text-solventum-dark mb-3" data-id="element-357">
                  The Solventum Clinical Masterclass
                </h2>
                <p className="text-solventum-dark/60" data-id="element-358">
                  A premium case-based learning library for Solventum — real
                  clinicians, real restorative cases, four experiential layers.
                </p>
              </div>

              <div className="space-y-8 text-solventum-dark/80 leading-relaxed" data-id="element-359">
                <section data-id="element-360">
                  <h3 className="font-semibold text-solventum-dark text-lg mb-2" data-id="element-361">
                    The idea
                  </h3>
                  <p data-id="element-362">
                    A premium Solventum Clinical Masterclass Library where real
                    clinicians walk practitioners through real restorative
                    cases, step by step. Each case opens with a
                    Masterclass-style introduction, presents the featured
                    clinician as the expert, introduces the clinical challenge,
                    then guides the viewer through the case from diagnosis to
                    technique to outcome. The product is never forced into the
                    story — Solventum appears naturally where it helped the
                    clinician solve the case.
                  </p>
                </section>

                <section data-id="element-363">
                  <h3 className="font-semibold text-solventum-dark text-lg mb-2" data-id="element-364">
                    Value for three audiences
                  </h3>
                  <ul className="space-y-3 list-disc list-outside ml-5" data-id="element-365">
                    <li data-id="element-366">
                      <span className="font-semibold text-solventum-dark" data-id="element-367">
                        Solventum
                      </span>{' '}
                      gets scalable engagement, sales enablement, and clinical
                      insight.
                    </li>
                    <li data-id="element-368">
                      <span className="font-semibold text-solventum-dark" data-id="element-369">
                        Clinicians (KOLs)
                      </span>{' '}
                      get visibility, authority, and a premium educational
                      stage.
                    </li>
                    <li data-id="element-370">
                      <span className="font-semibold text-solventum-dark" data-id="element-371">
                        Practitioners
                      </span>{' '}
                      get practical, case-based learning they can apply
                      chair-side.
                    </li>
                  </ul>
                </section>

                <section data-id="element-372">
                  <h3 className="font-semibold text-solventum-dark text-lg mb-2" data-id="element-373">
                    The four layers
                  </h3>
                  <ol className="space-y-4 list-decimal list-outside ml-5" data-id="element-374">
                    <li data-id="element-375">
                      <span className="font-semibold text-solventum-dark" data-id="element-376">
                        Masterclass Intro.
                      </span>{' '}
                      Each case opens cinematically: featured clinician, name,
                      location, and the clinical challenge they're about to
                      solve. Built like a Masterclass teaser — credibility from
                      the first frame.
                    </li>
                    <li data-id="element-377">
                      <span className="font-semibold text-solventum-dark" data-id="element-378">
                        The Clinical Core.
                      </span>{' '}
                      The case itself, told by the clinician: diagnosis →
                      treatment plan → isolation & prep → technique → finishing
                      → outcome. Solventum appears naturally where it helped
                      solve the case.
                    </li>
                    <li data-id="element-379">
                      <span className="font-semibold text-solventum-dark" data-id="element-380">
                        X-Ray Overlay.
                      </span>{' '}
                      An optional toggle reveals clinician credentials, case
                      details, materials used, and downloadable assets —
                      step-by-step PDFs, clinician bios, citations. Depth
                      without disruption.
                    </li>
                    <li data-id="element-381">
                      <span className="font-semibold text-solventum-dark" data-id="element-382">
                        The Case Library.
                      </span>{' '}
                      Practitioners browse by clinician they admire or by the
                      clinical challenge they're facing — anterior, posterior,
                      deep margin, layering, shade matching, Class V, and more.
                    </li>
                  </ol>
                </section>

                <section data-id="element-383">
                  <h3 className="font-semibold text-solventum-dark text-lg mb-2" data-id="element-384">
                    Why it works
                  </h3>
                  <p data-id="element-385">
                    Layer 1 hits clinician authority and instant engagement.
                    Layer 2 protects credibility by letting the clinician tell
                    the story and the product earn its place. Layer 3 rewards
                    curious viewers — and gives Solventum a memorable,
                    enablement-rich differentiator. Layer 4 then puts
                    practitioners in control, letting them browse the wider
                    library for the next case they need.
                  </p>
                </section>

                <section className="pt-4 border-t border-solventum-gray" data-id="element-386">
                  <h3 className="font-semibold text-solventum-dark text-lg mb-2" data-id="element-387">
                    Next steps
                  </h3>
                  <p data-id="element-388">
                    Curate the founding clinician roster, lock the first six
                    restorative cases, and begin production on a pilot Solventum
                    Clinical Masterclass series.
                  </p>
                </section>
              </div>

              <div className="mt-10 pt-6 border-t border-solventum-gray flex items-center justify-between text-sm text-solventum-dark/50" data-id="element-389">
                <span data-id="element-390">Prepared for Solventum</span>
                <button onClick={() => setOpen(false)} className="text-solventum-teal font-semibold hover:text-solventum-dark transition-colors" data-id="element-391">
                  Back to visual storyboard →
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}